/** Parse a color string into [r, g, b, a]. Supports #RGB, #RRGGBB, #RRGGBBAA, rgba(), and "transparent". */
export function parseColor(color: string): [number, number, number, number] {
  if (color === 'transparent') return [0, 0, 0, 0]

  // rgba(r, g, b, a)
  const rgbaMatch = color.match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/,
  )
  if (rgbaMatch) {
    return [
      Number(rgbaMatch[1]),
      Number(rgbaMatch[2]),
      Number(rgbaMatch[3]),
      rgbaMatch[4] != null ? Number(rgbaMatch[4]) : 1,
    ]
  }

  const h = color.replace('#', '')
  if (h.length === 3) {
    return [parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16), 1]
  }
  if (h.length === 8) {
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
      parseInt(h.slice(6, 8), 16) / 255,
    ]
  }
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16), 1]
}

/** Convert [r, g, b, a] to a CSS color string. */
function toColorString(r: number, g: number, b: number, a: number): string {
  if (a < 1) {
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(2)})`
  }
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/** Linearly interpolate between two colors. t ∈ [0, 1]. Supports transparent. */
export function interpolateColor(start: string, end: string, t: number): string {
  const [r1, g1, b1, a1] = parseColor(start)
  const [r2, g2, b2, a2] = parseColor(end)
  return toColorString(
    r1 + (r2 - r1) * t,
    g1 + (g2 - g1) * t,
    b1 + (b2 - b1) * t,
    a1 + (a2 - a1) * t,
  )
}

/** Interpolate with an optional mid-point: start → mid (t=0..0.5) → end (t=0.5..1). */
export function interpolateColorWithMid(
  start: string,
  mid: string,
  end: string,
  t: number,
): string {
  if (t <= 0.5) {
    return interpolateColor(start, mid, t * 2)
  }
  return interpolateColor(mid, end, (t - 0.5) * 2)
}

/** Compute t from value within [min, max], clamped to [0, 1]. */
export function normalizeValue(value: number, min: number, max: number): number {
  if (max === min) return 0.5
  return Math.max(0, Math.min(1, (value - min) / (max - min)))
}

/** Return '#fff' or '#000' depending on which has better contrast with bg. */
export function getContrastText(bgColor: string): string {
  const [r, g, b, a] = parseColor(bgColor)
  if (a < 0.5) return '' // transparent-ish — use default text color
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000' : '#fff'
}
